import { useState } from "react";
import { getCase } from "../../content/lookups.js";
import { getStage } from "../../content/stages.js";
import { mapCanvasHeight, mapDensity, mapGridTemplateColumns, mapRowHeight } from "../map/mapDensity.js";
import { edgeEndpoints } from "../map/mapLayout.js";
import { nodeIcon } from "../map/mapNodeIcon.js";
import { buildMapView } from "../map/mapView.js";
import type { AnalyticsSummary } from "./analyticsApi.js";

export type AnalyticsMetric = "dropped" | "gaveUp" | "total";
export const METRIC_LABELS: Record<AnalyticsMetric, string> = {
  dropped: "ゲームオーバー",
  gaveUp: "諦め・中断",
  total: "合計",
};

type NodeStat = AnalyticsSummary["nodes"][number];
type Props = { stageId: string; nodes: AnalyticsSummary["nodes"]; metric: AnalyticsMetric };

function valueFor(metric: AnalyticsMetric, node: NodeStat | undefined): number {
  if (!node) return 0;
  if (metric === "dropped") return node.gameoverCount;
  if (metric === "gaveUp") return node.abandonedCount;
  return node.gameoverCount + node.abandonedCount;
}

function formatValue(metric: AnalyticsMetric, stat: NodeStat | undefined): string {
  return stat ? String(valueFor(metric, stat)) : "—";
}

function tint(value: number, max: number): string {
  if (value === 0 || max === 0) return "rgba(139, 144, 154, 0.25)";
  const intensity = value / max;
  return `hsl(${Math.round(118 - Math.min(1, intensity) * 118)} 75% ${Math.round(42 + (1 - Math.min(1, intensity)) * 10)}%)`;
}

function NodeDetail({ nodeId, stat, onClose }: { nodeId: string; stat: NodeStat | undefined; onClose: () => void }) {
  return (
    <div className="analytics-map-detail" role="status">
      <div className="analytics-chart-detail-head">
        <b>{nodeId}</b>
        <button type="button" aria-label="詳細を閉じる" onClick={onClose}>×</button>
      </div>
      {stat
        ? <p>ゲームオーバー {stat.gameoverCount} ・ 諦め・中断 {stat.abandonedCount} ・ 合計 {stat.gameoverCount + stat.abandonedCount}</p>
        : <p>この期間の記録はありません。</p>}
    </div>
  );
}

export function AnalyticsMap({ stageId, nodes: allStats, metric }: Props) {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const { nodes, layout, edges, byId } = buildMapView(stageId);
  const stats = new Map(allStats.filter((node) => node.stageId === stageId).map((node) => [node.nodeId, node]));
  const max = Math.max(...nodes.map((node) => valueFor(metric, stats.get(node.id))), 0);
  const stage = getStage(stageId);
  const density = mapDensity(layout.columns);
  const rowHeight = mapRowHeight(density);
  const canvasHeight = mapCanvasHeight(layout.totalRows, density);

  return (
    <section className="analytics-map-card" aria-label={`${stage.name}のノード分析`}>
      <div className="analytics-map-scroll">
        <div className={`analytics-map is-${density}`} style={{ minHeight: canvasHeight }}>
          <svg className="analytics-map-lines" viewBox={`0 0 ${layout.columns} ${layout.totalRows}`} preserveAspectRatio="none" style={{ height: canvasHeight }} aria-hidden="true">
            {edges.map((edge) => {
              const from = byId.get(edge.from);
              const to = byId.get(edge.to);
              if (!from || !to) return null;
              return <line key={`${edge.from}-${edge.to}`} {...edgeEndpoints(from, to, layout)} />;
            })}
          </svg>
          <div className="analytics-map-grid" style={{ gridTemplateColumns: mapGridTemplateColumns(layout.columns), gridTemplateRows: `repeat(${layout.totalRows}, ${rowHeight}px)` }}>
            {nodes.map((node) => {
              const stat = stats.get(node.id);
              return (
                <button
                  type="button"
                  className={selectedId === node.id ? "analytics-map-node is-selected" : "analytics-map-node"}
                  key={node.id}
                  aria-label={`${node.id} の詳細`}
                  style={{ gridColumn: layout.gridColumnOf(node), gridRow: layout.gridRowOf(node) }}
                  onClick={() => setSelectedId(selectedId === node.id ? null : node.id)}
                >
                  <img
                    src={nodeIcon(node, node.caseId ? getCase(node.caseId) ?? null : null)}
                    alt=""
                    style={{ borderColor: tint(valueFor(metric, stat), max) }}
                  />
                  {node.kind !== "start" && <span className="analytics-node-values">{formatValue(metric, stat)}</span>}
                </button>
              );
            })}
          </div>
        </div>
      </div>
      {selectedId && <NodeDetail nodeId={selectedId} stat={stats.get(selectedId)} onClose={() => setSelectedId(null)} />}
      <p className="analytics-map-key">数字と枠線の色は「{METRIC_LABELS[metric]}」を表します。ノードをタップすると詳細が見られます。</p>
    </section>
  );
}
